import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Search, Filter, Edit2, Trash2, Edit, ChevronUp, ChevronDown, Users, Target, Loader, CheckSquare, Square, AlertTriangle, PlayCircle, BarChart, Calendar, Settings, MessageSquare, Sparkles, Save, Edit as EditIcon } from 'lucide-react';
import { CampaignDashboard } from './CampaignDashboard';
import { BuyCreditsModal } from './BuyCreditsModal';
// Move personality-based messages outside component scope
const personalityBasedMessages = {
  Analytical: ['I noticed your analytical approach to {{industry}} challenges. Would you be interested in data-driven solutions for cloud cost optimization?', 'Your methodical work in {{company}} caught my attention. Our platform provides detailed analytics that might interest you.', 'I appreciate your data-focused approach at {{company}}. Would you be open to discussing our analytical tools for cloud cost management?'],
  Collaborative: ['I saw your collaborative work at {{company}} and thought we might benefit from connecting about cloud infrastructure optimization.', "Your team-oriented approach at {{company}} is impressive. I'd love to discuss how our solution helps teams collaborate on cost management.", 'As someone who values partnership, I thought you might be interested in our collaborative approach to cloud cost optimization.'],
  Decisive: ['For decision-makers like yourself at {{company}}, our platform offers clear ROI on cloud infrastructure costs.', 'I noticed your decisive leadership at {{company}}. Would you be interested in a solution that provides actionable cost-saving insights?', 'Your direct approach to leadership at {{company}} is impressive. Our platform delivers straightforward cost-saving opportunities.'],
  Innovative: ["As an innovator at {{company}}, I thought you'd be interested in our AI-driven approach to cloud cost optimization.", 'Your forward-thinking work at {{company}} aligns with our innovative solution for reducing cloud infrastructure costs.', 'Fellow innovators like yourself understand the value of cutting-edge approaches to cost management. Would you be open to connecting?'],
  Strategic: ['I noticed your strategic approach at {{company}} and thought our cloud cost optimization platform might align with your long-term goals.', 'For strategic leaders like yourself, our platform provides both immediate savings and long-term cloud infrastructure optimization.', "Your strategic vision at {{company}} is impressive. I'd love to share how our solution fits into forward-thinking cloud management."]
};
const SortIcon = ({
  field
}: {
  field: string;
}) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  return <span className="inline-flex flex-col" data-id="element-547">
      <ChevronUp className={`h-3 w-3 -mb-1 ${sortField === field && sortDirection === 'asc' ? 'text-[#6366f1]' : 'text-gray-400'}`} data-id="element-548" />
      <ChevronDown className={`h-3 w-3 ${sortField === field && sortDirection === 'desc' ? 'text-[#6366f1]' : 'text-gray-400'}`} data-id="element-549" />
    </span>;
};
interface ProspectListProps {
  productDescription: string;
  linkedinFilters: string[];
  onStartCampaign: () => void;
  onCampaignScheduled: () => void;
  selectedDepth?: number;
  selectedAccuracy?: number;
  onDepthChange?: (depth: number) => void;
  onAccuracyChange?: (accuracy: number) => void;
}
export const ProspectList = forwardRef<any, ProspectListProps>(({
  productDescription,
  linkedinFilters,
  onStartCampaign,
  onCampaignScheduled,
  selectedDepth = 1,
  selectedAccuracy = 7,
  onDepthChange,
  onAccuracyChange
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [maxCredits, setMaxCredits] = useState(500);
  const [isEditingCredits, setIsEditingCredits] = useState(false);
  const [maxProspects, setMaxProspects] = useState(200);
  const [isEditingProspects, setIsEditingProspects] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showProspects, setShowProspects] = useState(false);
  const [selectedProspects, setSelectedProspects] = useState<number[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isCreatingMessages, setIsCreatingMessages] = useState(false);
  const [isSendingMessages, setIsSendingMessages] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [messageStatus, setMessageStatus] = useState<Record<number, string>>({});
  const [campaignStarted, setCampaignStarted] = useState(false);
  const [campaignProgress, setCampaignProgress] = useState(0);
  const [showCampaignProgress, setShowCampaignProgress] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleProgress, setScheduleProgress] = useState(0);
  const [showCampaignDashboard, setShowCampaignDashboard] = useState(false);
  const [campaignStartDate, setCampaignStartDate] = useState(new Date());
  const [isSystemPromptMinimized, setIsSystemPromptMinimized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(`You are an AI assistant tasked with generating personalized LinkedIn outreach messages. Create messages that feel authentic, relevant, and tailored to each prospect's personality type.
PERSONALIZATION GUIDELINES:
- Analytical prospects: Focus on data, ROI metrics, and evidence-based claims. Use precise language and specific numbers.
- Collaborative prospects: Emphasize community, teamwork, and shared success. Use inclusive language like "we" and "together".
- Decisive prospects: Be direct and concise. Lead with the most important benefit and clear next steps.
- Innovative prospects: Highlight cutting-edge aspects and forward-thinking benefits. Use language about innovation and future potential.
- Strategic prospects: Connect solution to long-term goals and competitive advantage. Discuss market positioning.
- Detail-oriented prospects: Provide comprehensive explanations with specific implementation details.
- Results-driven prospects: Focus on concrete outcomes, performance metrics, and tangible benefits.
- Technical prospects: Include specific technical details and integration capabilities. Use appropriate industry terminology.
GENERAL RULES:
1. Keep messages under 300 characters for initial connection requests
2. Avoid generic phrases like "I hope this message finds you well"
3. Reference specific aspects of the prospect's company or role
4. Include a clear, low-friction call to action
5. Maintain a professional but conversational tone
6. For follow-up messages, reference previous outreach without being pushy
The product is a cloud cost optimization platform for enterprises. Key value propositions include:
- Automated identification of cost-saving opportunities
- Average 30% reduction in cloud infrastructure costs
- Real-time monitoring and alerts
- Integration with all major cloud providers
- Custom reporting and analytics dashboard`);
  const [showMessageConfigModal, setShowMessageConfigModal] = useState(false);
  const [campaignMetrics, setCampaignMetrics] = useState({
    sent: 0,
    delivered: 0,
    opened: 0,
    replied: 0,
    connected: 0,
    total: 0
  });
  const [dailyStats, setDailyStats] = useState([{
    date: 'Mon',
    sent: 0,
    replies: 0
  }, {
    date: 'Tue',
    sent: 0,
    replies: 0
  }, {
    date: 'Wed',
    sent: 0,
    replies: 0
  }, {
    date: 'Thu',
    sent: 0,
    replies: 0
  }, {
    date: 'Fri',
    sent: 0,
    replies: 0
  }]);
  const itemsPerPage = 10;
  // Initialize with an empty array instead of sample data
  const [prospects, setProspects] = useState([]);
  // Filter prospects based on selected accuracy
  const filteredProspects = prospects.filter(prospect => prospect.accuracy >= selectedAccuracy);
  // Sort the filtered prospects
  const sortedProspects = [...filteredProspects].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });
  const totalPages = Math.ceil(filteredProspects.length / itemsPerPage);
  const currentProspects = sortedProspects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const generateSingleProspect = () => {
    // Company name lists based on common industries
    const techCompanies = ['Cloud Innovations', 'Data Dynamics', 'Tech Solutions', 'Digital Ventures', 'Smart Systems', 'Future Tech', 'CodeCraft', 'ByteWave', 'Quantum Computing', 'Insight Analytics'];
    const financeCompanies = ['Capital Finance', 'Wealth Partners', 'Investment Solutions', 'Financial Dynamics', 'Asset Management', 'Equity Group', 'Secure Banking', 'Profit Analytics', 'Money Matters', 'Fortune Capital'];
    const healthcareCompanies = ['Health Innovations', 'Care Solutions', 'Medical Systems', 'Wellness Group', 'Life Sciences', 'Patient Care', 'Health Analytics', 'BioTech Solutions', 'MediCorp', 'Healing Hands'];
    // Job titles for random generation
    const jobTitles = ['CEO', 'CTO', 'CFO', 'COO', 'VP of Sales', 'VP of Marketing', 'Director of Operations', 'Product Manager', 'Marketing Manager', 'Sales Manager', 'Head of Business Development', 'Director of Engineering', 'Head of HR', 'Director of Finance', 'Chief Product Officer'];
    // First names and last names for random generation
    const firstNames = ['James', 'Mary', 'Robert', 'Patricia', 'John', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Susan', 'Richard', 'Jessica', 'Joseph', 'Sarah', 'Thomas', 'Karen', 'Charles', 'Nancy', 'Emma', 'Olivia', 'Noah', 'Liam', 'Lucas', 'Chloe'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Jones', 'Brown', 'Davis', 'Miller', 'Wilson', 'Moore', 'Taylor', 'Anderson', 'Thomas', 'Jackson', 'White', 'Harris', 'Martin', 'Thompson', 'Garcia', 'Martinez', 'Robinson', 'Clark', 'Rodriguez', 'Lewis', 'Lee', 'Walker', 'Hall', 'Allen', 'Young', 'King', 'Wright'];
    // Email domains
    const emailDomains = ['gmail.com', 'outlook.com', 'yahoo.com', 'company.com', 'enterprise.com', 'business.net', 'corp.com', 'tech.io', 'example.com', 'mail.com'];
    // Determine which company list to use based on filters
    let companyList = techCompanies;
    if (linkedinFilters.some(filter => filter.toLowerCase().includes('finance') || filter.toLowerCase().includes('banking'))) {
      companyList = financeCompanies;
    } else if (linkedinFilters.some(filter => filter.toLowerCase().includes('health') || filter.toLowerCase().includes('medical'))) {
      companyList = healthcareCompanies;
    }
    // Personality types for random generation
    const personalityTypes = ['Analytical', 'Collaborative', 'Decisive', 'Innovative', 'Detail-oriented', 'Results-driven', 'Strategic', 'Practical', 'Visionary', 'Relationship-focused', 'Cautious', 'Ambitious', 'Methodical', 'Creative', 'Competitive', 'Diplomatic', 'Persuasive', 'Technical', 'Logical', 'Adaptable'];
    // Add connection message templates
    const connectionMessages = ['I noticed your work in cloud infrastructure and thought we could connect.', 'Given your role in tech, I think we might benefit from connecting.', "Your experience in the industry caught my attention. Let's connect!", "I'm reaching out based on our shared interest in enterprise solutions.", "I'd like to connect to discuss potential synergies in the tech space.", "Your profile stood out to me. I'd love to add you to my network.", "I'm expanding my network with professionals in your field.", "I saw your company's recent growth and would love to connect.", 'As fellow professionals in this space, I think we could benefit from connecting.', "I'm interested in your work at {company} and would like to connect."];
    const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const company = companyList[Math.floor(Math.random() * companyList.length)];
    const emailDomain = emailDomains[Math.floor(Math.random() * emailDomains.length)];
    const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${emailDomain}`;
    const jobTitle = jobTitles[Math.floor(Math.random() * jobTitles.length)];
    const personality = personalityTypes[Math.floor(Math.random() * personalityTypes.length)];
    const accuracy = (Math.random() * 2 + 7).toFixed(1); // Generate accuracy between 7.0 and 9.0
    // Generate a connection message based on personality
    let connectionMessage = '';
    // Check if there are personality-specific messages available
    if (personalityBasedMessages[personality] && Math.random() > 0.3) {
      // Use personality-based message 70% of the time when available
      const personalityMessages = personalityBasedMessages[personality];
      connectionMessage = personalityMessages[Math.floor(Math.random() * personalityMessages.length)];
    } else {
      // Use generic message
      connectionMessage = connectionMessages[Math.floor(Math.random() * connectionMessages.length)];
    }
    // Replace placeholders with actual values
    connectionMessage = connectionMessage.replace(/{{company}}/g, company).replace(/{{industry}}/g, 'cloud computing');
    return {
      name: `${firstName} ${lastName}`,
      email,
      jobTitle,
      company,
      personality,
      connectionMessage,
      accuracy: parseFloat(accuracy)
    };
  };
  // Function to generate multiple prospects based on filters and product description
  const generateProspects = () => {
    // Generate between 5-15 new prospects
    const count = Math.floor(Math.random() * 10) + 5;
    return Array(count).fill(0).map(() => generateSingleProspect());
  };
  // Handle clicking the "Discover Qualified Prospects" button
  const handleGenerateProspects = () => {
    setIsLoadingMore(true);
    setLoadingProgress(0);
    setShowProspects(true); // Always show prospects when generating
    // Keep track of how many prospects we've added during this discovery
    let addedProspectsCount = 0;
    const totalToAdd = Math.floor(Math.random() * 10) + 10; // Increase number of prospects to add
    // Simulate loading with progress and add prospects incrementally
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        const progressIncrement = Math.floor(Math.random() * 8) + 2;
        const newProgress = Math.min(prev + progressIncrement, 100);
        // Add a new prospect with each progress update
        if (addedProspectsCount < totalToAdd && newProgress < 95) {
          // Generate a single prospect
          const singleProspect = generateSingleProspect();
          // Add it to the prospects list with a slight delay for visual effect
          setTimeout(() => {
            setProspects(prevProspects => [...prevProspects, singleProspect]);
          }, 100);
          addedProspectsCount++;
        }
        // When loading is complete
        if (newProgress >= 100) {
          clearInterval(interval);
          // Make sure we've added all intended prospects
          if (addedProspectsCount < totalToAdd) {
            const remaining = totalToAdd - addedProspectsCount;
            // Add remaining prospects one by one with increasing delays for visual effect
            for (let i = 0; i < remaining; i++) {
              setTimeout(() => {
                const singleProspect = generateSingleProspect();
                setProspects(prevProspects => [...prevProspects, singleProspect]);
              }, 300 * i); // Stagger the additions with increasing delays
            }
          }
          setTimeout(() => {
            setIsLoadingMore(false);
          }, 500 + (totalToAdd - addedProspectsCount) * 300);
          return 100;
        }
        return newProgress;
      });
    }, 800); // Slower interval for more visible progress
  };
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    startCampaignAfterSchedule: () => {
      // First show the scheduling progress
      setIsScheduling(true);
      setScheduleProgress(0);
      // Simulate scheduling progress
      const scheduleInterval = setInterval(() => {
        setScheduleProgress(prev => {
          const newProgress = prev + Math.floor(Math.random() * 10) + 5;
          if (newProgress >= 100) {
            clearInterval(scheduleInterval);
            setTimeout(() => {
              setIsScheduling(false);
              // Then start the campaign creation process
              setIsCreatingMessages(true);
              setIsSendingMessages(false);
              setCreationProgress(0);
              setSendingProgress(0);
              setCampaignStarted(true);
              setShowCampaignProgress(true);
              setCampaignProgress(0);
              // Start campaign progress
              startCampaignProgress();
              // Set campaign start date to now
              setCampaignStartDate(new Date());
            }, 500);
            return 100;
          }
          return newProgress;
        });
      }, 200);
    },
    handleGenerateProspects: handleGenerateProspects
  }));
  const startCampaignProgress = () => {
    const campaignProgressInterval = setInterval(() => {
      setCampaignProgress(prev => {
        const newProgress = prev + 1;
        if (newProgress >= 100) {
          clearInterval(campaignProgressInterval);
          return 100;
        }
        return newProgress;
      });
    }, 200);
    const messageCreationInterval = setInterval(() => {
      setCreationProgress(prev => {
        const newProgress = prev + Math.floor(Math.random() * 5) + 1;
        if (newProgress >= 100) {
          clearInterval(messageCreationInterval);
          // Start sending messages after creation is complete
          setTimeout(() => {
            setIsCreatingMessages(false);
            setIsSendingMessages(true);
            simulateSendingMessages();
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 300);
  };
  // Reset showProspects when product description or filters change
  useEffect(() => {
    if (productDescription || linkedinFilters) {
      setShowProspects(false);
      setShowCampaignDashboard(false);
    }
  }, [productDescription, linkedinFilters]);
  // Simulate loading more prospects when the component mounts
  useEffect(() => {
    if (showProspects) {
      simulateLoadingProspects();
    }
  }, [showProspects]);
  const simulateLoadingProspects = () => {
    setIsLoadingMore(true);
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        const newProgress = prev + 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsLoadingMore(false);
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 500);
  };
  const simulateCampaignStart = () => {
    // Instead of starting the campaign directly, call the onStartCampaign prop
    // which will open the scheduling modal
    onStartCampaign();
  };
  const startCampaignAfterSchedule = () => {
    // Reset states
    setIsCreatingMessages(true);
    setIsSendingMessages(false);
    setCreationProgress(0);
    setSendingProgress(0);
    setCampaignStarted(true);
    setShowCampaignProgress(true);
    setCampaignProgress(0);
    // Start campaign progress
    const campaignProgressInterval = setInterval(() => {
      setCampaignProgress(prev => {
        const newProgress = prev + 1;
        if (newProgress >= 100) {
          clearInterval(campaignProgressInterval);
          return 100;
        }
        return newProgress;
      });
    }, 200);
    // Step 1: Create messages
    const messageCreationInterval = setInterval(() => {
      setCreationProgress(prev => {
        const newProgress = prev + Math.floor(Math.random() * 5) + 1;
        if (newProgress >= 100) {
          clearInterval(messageCreationInterval);
          // Start sending messages after creation is complete
          setTimeout(() => {
            setIsCreatingMessages(false);
            setIsSendingMessages(true);
            simulateSendingMessages();
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 300);
  };
  const simulateSendingMessages = () => {
    // Initialize all prospects as "to be sent" (yellow)
    const initialStatus: Record<number, string> = {};
    prospects.forEach((_, index) => {
      initialStatus[index] = 'pending';
    });
    setMessageStatus(initialStatus);
    // Initialize campaign metrics
    setCampaignMetrics({
      sent: 0,
      delivered: 0,
      opened: 0,
      replied: 0,
      connected: 0,
      total: prospects.length
    });
    // Initialize daily stats with random data
    const newDailyStats = dailyStats.map(day => ({
      date: day.date,
      sent: Math.floor(Math.random() * 15) + 5,
      replies: Math.floor(Math.random() * 5)
    }));
    setDailyStats(newDailyStats);
    // Send messages one by one with progress updates
    let currentIndex = 0;
    const totalProspects = prospects.length;
    const sendingInterval = setInterval(() => {
      if (currentIndex < totalProspects) {
        setMessageStatus(prev => ({
          ...prev,
          [currentIndex]: 'sent'
        }));
        // Update metrics as messages are sent
        setCampaignMetrics(prev => {
          const newSent = prev.sent + 1;
          const newDelivered = prev.delivered + (Math.random() > 0.1 ? 1 : 0);
          const newOpened = Math.min(prev.opened + (Math.random() > 0.5 ? 1 : 0), newDelivered);
          return {
            ...prev,
            sent: newSent,
            delivered: newDelivered,
            opened: newOpened
          };
        });
        if (Math.random() > 0.7) {
          setTimeout(() => {
            setMessageStatus(prev => ({
              ...prev,
              [currentIndex]: 'replied'
            }));
            // Update reply metrics
            setCampaignMetrics(prev => ({
              ...prev,
              replied: prev.replied + 1,
              connected: prev.connected + (Math.random() > 0.5 ? 1 : 0)
            }));
          }, Math.random() * 3000 + 1000);
        }
        currentIndex++;
        setSendingProgress(Math.floor(currentIndex / totalProspects * 100));
      } else {
        clearInterval(sendingInterval);
        setTimeout(() => {
          setIsSendingMessages(false);
          // Show the campaign dashboard
          setShowCampaignDashboard(true);
          onCampaignScheduled(); // Notify parent that campaign is complete
        }, 1000);
      }
    }, 500);
  };
  const getStatusColor = (status: string, index: number) => {
    if (!campaignStarted) return '';
    switch (messageStatus[index]) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'replied':
        return 'bg-green-100 text-green-800';
      default:
        return '';
    }
  };
  const handleMaxCreditsChange = e => {
    setMaxCredits(e.target.value);
  };
  const handleMaxCreditsBlur = () => {
    setIsEditingCredits(false);
  };
  const handleMaxCreditsKeyDown = e => {
    if (e.key === 'Enter') {
      setIsEditingCredits(false);
    }
  };
  const handleMaxProspectsChange = e => {
    setMaxProspects(e.target.value);
  };
  const handleMaxProspectsBlur = () => {
    setIsEditingProspects(false);
  };
  const handleMaxProspectsKeyDown = e => {
    if (e.key === 'Enter') {
      setIsEditingProspects(false);
    }
  };
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const toggleSelectAll = () => {
    const currentPageIndexes = currentProspects.map((_, i) => (currentPage - 1) * itemsPerPage + i);
    const areAllSelected = currentPageIndexes.every(index => selectedProspects.includes(index));
    if (areAllSelected) {
      setSelectedProspects(prev => prev.filter(index => !currentPageIndexes.includes(index)));
    } else {
      setSelectedProspects(prev => [...new Set([...prev, ...currentPageIndexes])]);
    }
  };
  const deleteAllProspects = () => {
    setProspects([]);
    setSelectedProspects([]);
  };
  const deleteSelectedProspects = () => {
    const newProspects = prospects.filter((_, index) => !selectedProspects.includes(index));
    setProspects(newProspects);
    setSelectedProspects([]);
  };
  const toggleSelectProspect = (index: number) => {
    setSelectedProspects(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };
  const isCurrentPageItemSelected = (index: number) => selectedProspects.includes((currentPage - 1) * itemsPerPage + index);
  const areAllCurrentPageItemsSelected = currentProspects.every((_, index) => isCurrentPageItemSelected(index));
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  return <div className="space-y-6" data-id="element-550">
        {/* Loading progress card - moved before Campaign Prospects */}
        {isLoadingMore && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-551">
            <div className="mb-4" data-id="element-552">
              <div className="flex items-center justify-between mb-1" data-id="element-553">
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-554">
                  <Loader size={16} className="animate-spin text-[#6366f1]" data-id="element-555" />
                  <span data-id="element-556">Discovering qualified prospects...</span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-557">
                  {loadingProgress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-558">
                <div className="bg-[#6366f1] h-2 rounded-full transition-all duration-300 ease-in-out" style={{
            width: `${loadingProgress}%`
          }} data-id="element-559"></div>
              </div>
            </div>
          </div>}

        {/* Campaign Prospects card - Always show with full details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-560">
          <div className="p-4 border-b border-gray-200" data-id="element-561">
            <div className="flex justify-between items-center mb-4" data-id="element-562">
              <div data-id="element-563">
                <h3 className="font-medium text-gray-700 mb-3" data-id="element-564">
                  Campaign Prospects
                </h3>
                {/* Campaign info - always visible */}
                <div className="flex items-center gap-3" data-id="element-565">
                  {/* Prospects count */}
                  <div className="text-sm text-gray-600 flex items-center" data-id="element-566">
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200" data-id="element-567">
                      <span className="font-medium text-gray-900" data-id="element-568">
                        {prospects.length}
                      </span>
                      <span className="text-gray-500" data-id="element-569">/1K</span>
                      <span data-id="element-570">prospects</span>
                      {prospects.length > 0 && <button onClick={() => setIsEditingProspects(true)} className="text-gray-500 hover:text-[#6366f1] ml-1" data-id="element-571">
                          <Edit size={14} data-id="element-572" />
                        </button>}
                    </div>
                    {isEditingProspects && <div className="absolute mt-1 bg-white border border-gray-200 rounded-md shadow-md p-2 z-10" data-id="element-573">
                        <div className="flex items-center gap-2" data-id="element-574">
                          <span className="text-xs text-gray-500" data-id="element-575">
                            Max prospects:
                          </span>
                          <input type="number" value={maxProspects} onChange={handleMaxProspectsChange} onBlur={handleMaxProspectsBlur} onKeyDown={handleMaxProspectsKeyDown} className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" autoFocus data-id="element-576" />
                        </div>
                      </div>}
                  </div>
                  {/* Depth - Fixed at 3 */}
                  <div className="text-sm text-gray-600" data-id="element-577">
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200" data-id="element-578">
                      <span data-id="element-579">Depth:</span>
                      <span className="font-medium text-gray-900" data-id="element-580">3</span>
                    </div>
                  </div>
                  {/* Accuracy - Fixed at 9 */}
                  <div className="text-sm text-gray-600" data-id="element-581">
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200" data-id="element-582">
                      <span data-id="element-583">Accuracy:</span>
                      <span className="font-medium text-gray-900" data-id="element-584">9</span>
                    </div>
                  </div>
                </div>
                {/* Delete actions - only show when there are prospects */}
                {prospects.length > 0 && <div className="flex items-center gap-2 mt-3" data-id="element-585">
                    {selectedProspects.length > 0 && <button onClick={deleteSelectedProspects} className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1" data-id="element-586">
                        <Trash2 size={16} data-id="element-587" />
                        Delete Selected ({selectedProspects.length})
                      </button>}
                    <button onClick={deleteAllProspects} className="text-sm text-gray-600 hover:text-red-600 flex items-center gap-1" data-id="element-588">
                      <AlertTriangle size={16} data-id="element-589" />
                      Delete All
                    </button>
                  </div>}
              </div>
              {/* Credits section - always visible */}
              <div className="flex items-center gap-3" data-id="element-590">
                <div className="relative group" data-id="element-591">
                  <div className="text-sm text-gray-600 flex items-center gap-1 cursor-help" data-id="element-592">
                    <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border border-gray-200" data-id="element-593">
                      <span className="font-medium text-gray-900" data-id="element-594">
                        {prospects.length * selectedDepth}
                      </span>
                      <span className="text-gray-500" data-id="element-595">/500</span>
                      <span data-id="element-596">credits</span>
                    </div>
                    <div className="absolute invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-1 px-2 -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10" data-id="element-597">
                      1 credit per message (prospects × depth)
                      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" data-id="element-598"></div>
                    </div>
                  </div>
                </div>
                <button className="text-sm bg-[#6366f1] text-white px-3 py-1.5 rounded-md hover:bg-[#5457e5] font-medium transition-colors" onClick={() => setIsBuyCreditsModalOpen(true)} data-id="element-599">
                  Buy More Credits
                </button>
              </div>
            </div>

            {/* Campaign Progress Bar */}
            {showCampaignProgress && <div className="mb-4" data-id="element-600">
                <div className="flex items-center justify-between mb-1" data-id="element-601">
                  <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-602">
                    <BarChart size={16} className="text-[#6366f1]" data-id="element-603" />
                    <span data-id="element-604">Campaign Progress</span>
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-605">
                    {campaignProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-606">
                  <div className="bg-[#d5ff00] h-2 rounded-full transition-all duration-300 ease-in-out" style={{
              width: `${campaignProgress}%`
            }} data-id="element-607"></div>
                </div>
              </div>}

            {/* Search and filter - always show */}
            <div className="flex gap-4 mt-4" data-id="element-608">
              <div className="flex-1 relative" data-id="element-609">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-610" />
                <input type="text" placeholder="Search prospects..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} disabled={prospects.length === 0} data-id="element-611" />
              </div>
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" disabled={prospects.length === 0} data-id="element-612">
                <Filter size={20} data-id="element-613" />
                Filter
              </button>
            </div>

            {/* Showing results count - always show */}
            <div className="flex justify-between items-center mt-4 text-sm text-gray-500" data-id="element-614">
              <div data-id="element-615">
                Showing{' '}
                {filteredProspects.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                -
                {Math.min(currentPage * itemsPerPage, filteredProspects.length)}{' '}
                of {filteredProspects.length} prospects
                {filteredProspects.length < prospects.length && <span className="ml-1 text-gray-400" data-id="element-616">
                    (filtered from {prospects.length} total)
                  </span>}
              </div>
              {selectedProspects.length > 0 && <div className="text-gray-600" data-id="element-617">
                  {selectedProspects.length} selected
                </div>}
            </div>
          </div>

          {/* Table - always show */}
          <div className="overflow-x-auto" data-id="element-618">
            <table className="w-full" data-id="element-619">
              <thead className="bg-gray-50" data-id="element-620">
                <tr data-id="element-621">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-622">
                    <button onClick={toggleSelectAll} className="focus:outline-none" disabled={prospects.length === 0} data-id="element-623">
                      {areAllCurrentPageItemsSelected ? <CheckSquare size={16} className="text-[#6366f1]" data-id="element-624" /> : <Square size={16} data-id="element-625" />}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')} data-id="element-626">
                    <div className="flex items-center gap-1" data-id="element-627">
                      Name
                      <SortIcon field="name" data-id="element-628" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('jobTitle')} data-id="element-629">
                    <div className="flex items-center gap-1" data-id="element-630">
                      Job Title
                      <SortIcon field="jobTitle" data-id="element-631" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('company')} data-id="element-632">
                    <div className="flex items-center gap-1" data-id="element-633">
                      Company
                      <SortIcon field="company" data-id="element-634" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('personality')} data-id="element-635">
                    <div className="flex items-center gap-1" data-id="element-636">
                      Personality
                      <SortIcon field="personality" data-id="element-637" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('connectionMessage')} data-id="element-638">
                    <div className="flex items-center gap-1" data-id="element-639">
                      Connection Message
                      <SortIcon field="connectionMessage" data-id="element-640" />
                    </div>
                  </th>
                  {!campaignStarted && selectedDepth > 1 && <>
                      {Array.from({
                  length: selectedDepth - 1
                }).map((_, index) => <th key={`message-${index + 2}`} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-641">
                          Message {index + 2}
                        </th>)}
                    </>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('accuracy')} data-id="element-642">
                    <div className="flex items-center gap-1" data-id="element-643">
                      Accuracy
                      <SortIcon field="accuracy" data-id="element-644" />
                    </div>
                  </th>
                  {campaignStarted && <>
                      {Array.from({
                  length: selectedDepth
                }).map((_, index) => <th key={`message-${index + 1}`} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-645">
                          Message {index + 1}
                        </th>)}
                    </>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-646">
                {prospects.length === 0 ? <tr data-id="element-647">
                    <td colSpan={8} className="px-6 py-8" data-id="element-648">
                      {/* Empty - no message */}
                    </td>
                  </tr> : currentProspects.map((prospect, index) => <tr key={index} className={`${isCurrentPageItemSelected(index) ? 'bg-blue-50' : ''} ${getStatusColor(messageStatus[(currentPage - 1) * itemsPerPage + index], (currentPage - 1) * itemsPerPage + index)}`} data-id="element-649">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-650">
                        <button onClick={() => toggleSelectProspect(index)} className="focus:outline-none" data-id="element-651">
                          {isCurrentPageItemSelected(index) ? <CheckSquare size={16} className="text-[#6366f1]" data-id="element-652" /> : <Square size={16} data-id="element-653" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-654">
                        {prospect.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-655">
                        {prospect.jobTitle}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-656">
                        {prospect.company}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-657">
                        {prospect.personality || 'Analytical'}
                      </td>
                      <td className="px-6 py-4 max-w-[300px] truncate" data-id="element-658">
                        {campaignStarted ? prospect.connectionMessage : <span className="text-gray-400 italic" data-id="element-659">
                            Not created yet
                          </span>}
                      </td>
                      {!campaignStarted && selectedDepth > 1 && <>
                          {Array.from({
                  length: selectedDepth - 1
                }).map((_, msgIndex) => <td key={`msg-${msgIndex}`} className="px-6 py-4 text-gray-400 italic" data-id="element-660">
                              Not created yet
                            </td>)}
                        </>}
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-661">
                        {prospect.accuracy}
                      </td>
                      {campaignStarted && <>
                          {Array.from({
                  length: selectedDepth
                }).map((_, msgIndex) => <td key={`msg-${msgIndex}`} className="px-6 py-4 whitespace-nowrap" data-id="element-662">
                              <span className={`px-2 py-1 text-xs rounded-full ${messageStatus[(currentPage - 1) * itemsPerPage + index] === 'pending' ? 'bg-yellow-100 text-yellow-800' : messageStatus[(currentPage - 1) * itemsPerPage + index] === 'sent' ? 'bg-blue-100 text-blue-800' : messageStatus[(currentPage - 1) * itemsPerPage + index] === 'replied' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-663">
                                {messageStatus[(currentPage - 1) * itemsPerPage + index] === 'pending' ? 'To be sent' : messageStatus[(currentPage - 1) * itemsPerPage + index] === 'sent' ? 'Sent' : messageStatus[(currentPage - 1) * itemsPerPage + index] === 'replied' ? 'Replied' : 'Not started'}
                              </span>
                            </td>)}
                        </>}
                    </tr>)}
              </tbody>
            </table>
          </div>

          {/* Pagination - always show */}
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-664">
            <div className="flex-1 flex justify-between sm:hidden" data-id="element-665">
              <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-666">
                Previous
              </button>
              <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-667">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-668">
              <div data-id="element-669">
                <p className="text-sm text-gray-700" data-id="element-670">
                  Showing{' '}
                  <span className="font-medium" data-id="element-671">
                    {filteredProspects.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium" data-id="element-672">
                    {Math.min(currentPage * itemsPerPage, filteredProspects.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium" data-id="element-673">
                    {filteredProspects.length}
                  </span>{' '}
                  results
                </p>
              </div>
              <div data-id="element-674">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-675">
                  <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'}`} data-id="element-676">
                    <span className="sr-only" data-id="element-677">Previous</span>
                    &larr;
                  </button>
                  {totalPages > 0 && [...Array(totalPages)].map((_, i) => <button key={i} onClick={() => handlePageChange(i + 1)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1 ? 'z-10 bg-[#6366f1] border-[#6366f1] text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} data-id="element-678">
                        {i + 1}
                      </button>)}
                  <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'}`} data-id="element-679">
                    <span className="sr-only" data-id="element-680">Next</span>
                    &rarr;
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Schedule Campaign button */}
        {showProspects && !showCampaignDashboard && <div className="flex justify-center flex-col items-center gap-3" data-id="element-681">
            {/* System Prompt Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col w-full" data-id="element-682">
              <div className="p-3 border-b border-gray-200 flex items-center justify-between cursor-pointer" onClick={() => setIsSystemPromptMinimized(!isSystemPromptMinimized)} data-id="element-683">
                <div className="flex items-center gap-2" data-id="element-684">
                  <Sparkles className="h-4 w-4 text-blue-500" data-id="element-685" />
                  <h3 className="font-medium text-gray-700" data-id="element-686">System Prompt</h3>
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-md" data-id="element-687">
                    Power User
                  </span>
                </div>
              </div>
              {!isSystemPromptMinimized && <div className="p-4" data-id="element-688">
                  <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} className="w-full h-32 p-3 bg-gray-50 rounded-md text-sm text-gray-700 resize-none focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" data-id="element-689" />
                </div>}
            </div>
            <button onClick={simulateCampaignStart} className={`px-6 py-3 bg-[#d5ff00] text-gray-900 rounded-lg hover:bg-[#c0e600] font-semibold flex items-center gap-2 shadow-sm ${isCreatingMessages || isSendingMessages || isScheduling ? 'opacity-80' : ''}`} disabled={isCreatingMessages || isSendingMessages || isScheduling} data-id="element-690">
              <PlayCircle size={20} data-id="element-691" />
              Schedule Campaign
            </button>
          </div>}

        {/* Message Configuration Modal */}
        {showMessageConfigModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-692">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full" data-id="element-693">
              <div className="flex justify-between items-center mb-4" data-id="element-694">
                <h3 className="text-xl font-medium text-gray-900" data-id="element-695">
                  Configure Message Templates
                </h3>
                <button onClick={() => setShowMessageConfigModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-696">
                  &times;
                </button>
              </div>
              <div className="mb-4" data-id="element-697">
                <p className="text-sm text-gray-600 mb-2" data-id="element-698">
                  Customize the message templates that will be used for your
                  outreach campaign.
                </p>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4" data-id="element-699">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between" data-id="element-700">
                    <div className="flex items-center gap-2" data-id="element-701">
                      <MessageSquare className="h-4 w-4 text-purple-500" data-id="element-702" />
                      <h3 className="font-medium text-gray-700" data-id="element-703">
                        Connection Message Template
                      </h3>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-704">
                    <textarea placeholder="Enter your connection message template..." className="w-full h-32 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" defaultValue={`Hi {{first_name}}, I noticed your work at {{company}} and your role as {{job_title}}. I thought we could connect as I believe our solution could help with your cloud infrastructure costs. Would you be open to a quick chat?`} data-id="element-705" />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-706">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between" data-id="element-707">
                    <div className="flex items-center gap-2" data-id="element-708">
                      <MessageSquare className="h-4 w-4 text-purple-500" data-id="element-709" />
                      <h3 className="font-medium text-gray-700" data-id="element-710">
                        Follow-up Message Template
                      </h3>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-711">
                    <textarea placeholder="Enter your follow-up message template..." className="w-full h-32 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#6366f1] focus:border-transparent outline-none" defaultValue={`Hi {{first_name}}, I wanted to follow up on my previous message. Our solution has helped companies like {{company}} reduce their cloud costs by 30% on average. Would you be interested in learning more?`} data-id="element-712" />
                  </div>
                </div>
              </div>
              <div className="flex justify-between" data-id="element-713">
                <button onClick={() => {
            // Reset to defaults logic would go here
          }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-714">
                  Reset to Default
                </button>
                <div className="flex gap-2" data-id="element-715">
                  <button onClick={() => {
              // Reset to defaults logic would go here
            }} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-716">
                    Cancel
                  </button>
                  <button onClick={() => {
              // Save changes
              setShowMessageConfigModal(false);
            }} className="px-4 py-2 bg-[#6366f1] text-white rounded-md hover:bg-[#5457e5]" data-id="element-717">
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Scheduling progress card */}
        {isScheduling && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4" data-id="element-718">
            <div className="mb-4" data-id="element-719">
              <div className="flex items-center justify-between mb-1" data-id="element-720">
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-721">
                  <Calendar size={16} className="text-[#6366f1]" data-id="element-722" />
                  <span data-id="element-723">Scheduling campaign...</span>
                </div>
                <span className="text-xs text-gray-500" data-id="element-724">
                  {scheduleProgress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-725">
                <div className="bg-[#d5ff00] h-2 rounded-full transition-all duration-300 ease-in-out" style={{
            width: `${scheduleProgress}%`
          }} data-id="element-726"></div>
              </div>
            </div>
          </div>}

        {/* Message creation and sending progress */}
        {showProspects && (isCreatingMessages || isSendingMessages) && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4" data-id="element-727">
            <div className="space-y-4" data-id="element-728">
              {isCreatingMessages && <div data-id="element-729">
                  <div className="flex items-center justify-between mb-1" data-id="element-730">
                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-731">
                      <Loader size={16} className="animate-spin text-[#6366f1]" data-id="element-732" />
                      <span data-id="element-733">Creating personalized messages...</span>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-734">
                      {creationProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-735">
                    <div className="bg-[#6366f1] h-2 rounded-full transition-all duration-300 ease-in-out" style={{
              width: `${creationProgress}%`
            }} data-id="element-736"></div>
                  </div>
                </div>}
              {isSendingMessages && <div data-id="element-737">
                  <div className="flex items-center justify-between mb-1" data-id="element-738">
                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-739">
                      <Loader size={16} className="animate-spin text-[#6366f1]" data-id="element-740" />
                      <span data-id="element-741">Sending messages to prospects...</span>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-742">
                      {sendingProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-743">
                    <div className="bg-[#6366f1] h-2 rounded-full transition-all duration-300 ease-in-out" style={{
              width: `${sendingProgress}%`
            }} data-id="element-744"></div>
                  </div>
                </div>}
            </div>
          </div>}

        {/* Campaign Dashboard */}
        {showCampaignDashboard && <CampaignDashboard campaignName="LinkedIn Outreach Campaign" startDate={campaignStartDate} metrics={campaignMetrics} dailyStats={dailyStats} data-id="element-745" />}

        {/* Buy Credits Modal */}
        <BuyCreditsModal isOpen={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} data-id="element-746" />
      </div>;
});